import React from 'react'
import Cropper from 'react-easy-crop/index.js'

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

async function removeFlatBackground(imageSrc) {
    const image = await new Promise((resolve, reject) => {
        const nextImage = new Image()
        nextImage.crossOrigin = 'anonymous'
        nextImage.onload = () => resolve(nextImage)
        nextImage.onerror = reject
        nextImage.src = imageSrc
    })
    const canvas = document.createElement('canvas')
    canvas.width = image.width
    canvas.height = image.height
    const context = canvas.getContext('2d')
    context.drawImage(image, 0, 0)

    const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
    const pixels = imageData.data
    const samplePoints = [
        [0, 0],
        [canvas.width - 1, 0],
        [0, canvas.height - 1],
        [canvas.width - 1, canvas.height - 1]
    ]

    const average = samplePoints.reduce((accumulator, point) => {
        const index = (point[1] * canvas.width + point[0]) * 4
        accumulator.r += pixels[index]
        accumulator.g += pixels[index + 1]
        accumulator.b += pixels[index + 2]
        return accumulator
    }, { r: 0, g: 0, b: 0 })

    const target = {
        r: average.r / samplePoints.length,
        g: average.g / samplePoints.length,
        b: average.b / samplePoints.length
    }
    const tolerance = 90
    const featherStart = 48

    for (let index = 0; index < pixels.length; index += 4) {
        const distance = Math.sqrt(
            Math.pow(pixels[index] - target.r, 2) +
            Math.pow(pixels[index + 1] - target.g, 2) +
            Math.pow(pixels[index + 2] - target.b, 2)
        )

        if (distance <= featherStart) {
            pixels[index + 3] = 0
        } else if (distance <= tolerance) {
            pixels[index + 3] = Math.round(((distance - featherStart) / (tolerance - featherStart)) * 255)
        }
    }

    context.putImageData(imageData, 0, 0)
    return canvas.toDataURL('image/png')
}

class LogoEditorModal extends React.Component {
    cropViewport = { width: 520, height: 520 }

    state = {
        source: this.props.image || '',
        crop: { x: 0, y: 0 },
        zoom: this.props.logoScale || 1,
        croppedAreaPixels: null,
        processing: false,
        error: '',
        logoBg: this.props.logoBg || '',
        logoFrame: this.props.logoFrame || 'none'
    }

    _defaultCropState(image) {
        return {
            source: image || '',
            crop: {
                x: Number(this.props.logoOffsetX || 0) * this.cropViewport.width,
                y: Number(this.props.logoOffsetY || 0) * this.cropViewport.height
            },
            zoom: Number(this.props.logoScale || 1)
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            this.props.open &&
            (
                !prevProps.open ||
                prevProps.image !== this.props.image ||
                prevProps.logoBg !== this.props.logoBg ||
                prevProps.logoFrame !== this.props.logoFrame ||
                prevProps.logoScale !== this.props.logoScale ||
                prevProps.logoOffsetX !== this.props.logoOffsetX ||
                prevProps.logoOffsetY !== this.props.logoOffsetY
            )
        ) {
            this.setState({
                ...this._defaultCropState(this.props.image),
                croppedAreaPixels: null,
                processing: false,
                error: '',
                logoBg: this.props.logoBg || '',
                logoFrame: this.props.logoFrame || 'none'
            })
            return
        }
    }

    async _handleReplacementFile(event) {
        const file = event.target.files && event.target.files[0]
        if (!file) return
        try {
            const nextSource = await readFileAsDataUrl(file)
            this.setState({ source: nextSource, crop: { x: 0, y: 0 }, zoom: 1, croppedAreaPixels: null, error: '' })
        } catch (error) {
            this.setState({ error: 'Unable to load that image file.' })
        }
    }

    async _removeBackground() {
        if (!this.state.source) return
        this.setState({ processing: true, error: '' })
        try {
            const outputUrl = await removeFlatBackground(this.state.source)
            this.setState({ source: outputUrl, crop: { x: 0, y: 0 }, zoom: 1, croppedAreaPixels: null, processing: false })
        } catch (error) {
            this.setState({ processing: false, error: 'Background removal works best on flatter backgrounds and could not complete for this image.' })
        }
    }

    async _apply() {
        if (!this.state.source) return
        this.setState({ processing: true, error: '' }, () => {
            this.props.onApply({
                dataUrl: this.state.source,
                logoBg: this.state.logoBg,
                logoFrame: this.state.logoFrame,
                logoScale: this.state.zoom,
                logoOffsetX: this.state.crop.x / this.cropViewport.width,
                logoOffsetY: this.state.crop.y / this.cropViewport.height
            })
            this.setState({ processing: false })
        })
    }

    _fillToSpace() {
        this.setState({
            crop: { x: 0, y: 0 },
            zoom: 1
        })
    }

    _logoFrameClass(frame) {
        if (frame === 'circle') return 'rounded-full'
        if (frame === 'rounded') return 'rounded-[28px]'
        if (frame === 'square') return 'rounded-none'
        return ''
    }

    render() {
        if (!this.props.open) return null
        const previewTransform = `translate(${Number(this.state.crop.x / this.cropViewport.width) * 100}%, ${Number(this.state.crop.y / this.cropViewport.height) * 100}%) scale(${this.state.zoom})`

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-6 backdrop-blur-sm">
                <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-slate-800 bg-slate-950 shadow-2xl">
                    <div className="flex items-start justify-between gap-4 border-b border-white/10 px-6 py-5">
                        <div>
                            <h2 className="text-xl font-semibold text-white">Edit logo</h2>
                            <p className="mt-1 text-sm text-slate-300">Crop the logo, try background removal, and choose presentation styling before saving it into the client draft.</p>
                        </div>
                        <button type="button" className="inline-flex h-10 items-center justify-center rounded-xl border border-white/10 px-4 text-sm font-semibold text-slate-200 transition hover:bg-white/5" onClick={this.props.onClose}>
                            Close
                        </button>
                    </div>

                    <div className="min-h-0 flex-1 lg:flex">
                        <div className="relative min-h-[320px] bg-slate-900 lg:min-h-0 lg:flex-1">
                            {this.state.source ?
                                <Cropper
                                            key={this.state.source ? this.state.source.slice(0, 64) : 'empty'}
                                            image={this.state.source}
                                            crop={this.state.crop}
                                            zoom={this.state.zoom}
                                            aspect={1}
                                            cropSize={this.cropViewport}
                                            minZoom={0.2}
                                    maxZoom={4}
                                    showGrid={true}
                                    cropShape="rect"
                                    onCropChange={(crop) => this.setState({ crop })}
                                    onZoomChange={(zoom) => this.setState({ zoom })}
                                    onCropComplete={(_, croppedAreaPixels) => {
                                        this.setState({ croppedAreaPixels })
                                    }}
                                />
                                :
                                <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-400">
                                    Choose a logo image to start editing.
                                </div>}
                        </div>

                        <div className="flex min-h-0 flex-col border-t border-white/10 bg-slate-950 lg:w-[360px] lg:border-l lg:border-t-0">
                            <div className="space-y-4 overflow-y-auto px-6 py-5">
                                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                    <div className="text-sm font-semibold text-white">Quick adjust</div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            className="rounded-xl border border-white/10 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/5"
                                            onClick={() => this._fillToSpace()}
                                        >
                                            Fill to space
                                        </button>
                                        <button
                                            type="button"
                                            className="rounded-xl border border-white/10 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/5"
                                            onClick={() => this.setState({ crop: { x: 0, y: 0 }, zoom: 0.6 })}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>

                                <label className="block">
                                    <div className="text-sm font-semibold text-white">Replace image</div>
                                    <input type="file" accept="image/*" className="mt-3 block w-full text-sm text-slate-300 file:mr-4 file:rounded-xl file:border-0 file:bg-sky-500 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-slate-950 hover:file:bg-sky-400" onChange={(event) => this._handleReplacementFile(event)} />
                                </label>

                                <div>
                                    <div className="text-sm font-semibold text-white">Zoom</div>
                                    <input type="range" min="0.2" max="4" step="0.01" value={this.state.zoom} onChange={(event) => this.setState({ zoom: Number(event.target.value) })} className="mt-3 w-full" />
                                    <div className="mt-2 text-xs text-slate-400">You can zoom out to make the logo smaller inside the crop area.</div>
                                </div>

                                <div>
                                    <div className="text-sm font-semibold text-white">Presentation preview</div>
                                    <div className="mt-3 rounded-2xl border border-white/10 bg-slate-900 p-5">
                                        <div className="flex items-center justify-center">
                                            <div
                                                className={'relative h-44 w-44 overflow-hidden shadow-inner ' + this._logoFrameClass(this.state.logoFrame)}
                                                style={this.state.logoBg ? { background: this.state.logoBg } : { background: 'transparent' }}
                                            >
                                                {this.state.source ?
                                                    <img
                                                        src={this.state.source}
                                                        alt="Logo presentation preview"
                                                        className="absolute inset-0 h-full w-full object-contain"
                                                        style={{ transform: previewTransform, transformOrigin: 'center center' }}
                                                    />
                                                    :
                                                    <div className="text-xs text-slate-500">No logo loaded</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-2 text-xs leading-5 text-slate-400">
                                        Background color and frame shape are public-page presentation styling. If the source image already has a solid white box in it, remove the background first so the color can show through cleanly.
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-xs leading-5 text-amber-100">
                                    Logo crop is always rectangular. Use <span className="font-semibold">Logo frame shape</span> below to control square, rounded, or circle presentation on the public page.
                                </div>

                                <div>
                                    <div className="text-sm font-semibold text-white">Logo background</div>
                                    <div className="mt-3 flex items-center gap-3">
                                        <input
                                            type="color"
                                            value={this.state.logoBg || '#ffffff'}
                                            onChange={(event) => this.setState({ logoBg: event.target.value })}
                                            className="h-11 w-12 rounded-lg border border-white/10 bg-transparent p-1"
                                        />
                                        <button
                                            type="button"
                                            className="rounded-xl border border-white/10 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/5"
                                            onClick={() => this.setState({ logoBg: '' })}
                                        >
                                            No background
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <div className="text-sm font-semibold text-white">Logo frame shape</div>
                                    <div className="mt-3 grid grid-cols-4 gap-2">
                                        {[
                                            {
                                                value: 'none',
                                                label: 'No frame',
                                                icon: (
                                                    <span className="relative block h-5 w-5">
                                                        <span className="absolute left-1/2 top-0 h-5 w-0.5 -translate-x-1/2 rotate-45 bg-current" />
                                                        <span className="absolute left-1/2 top-0 h-5 w-0.5 -translate-x-1/2 -rotate-45 bg-current" />
                                                    </span>
                                                )
                                            },
                                            {
                                                value: 'square',
                                                label: 'Square frame',
                                                icon: <span className="block h-5 w-5 border-2 border-current" />
                                            },
                                            {
                                                value: 'rounded',
                                                label: 'Rounded frame',
                                                icon: <span className="block h-5 w-5 rounded-[7px] border-2 border-current" />
                                            },
                                            {
                                                value: 'circle',
                                                label: 'Circle frame',
                                                icon: <span className="block h-5 w-5 rounded-full border-2 border-current" />
                                            }
                                        ].map((frame) => (
                                            <button
                                                key={frame.value}
                                                type="button"
                                                title={frame.label}
                                                aria-label={frame.label}
                                                onClick={() => this.setState({ logoFrame: frame.value })}
                                                className={'flex items-center justify-center rounded-xl border px-3 py-2.5 text-sm font-semibold transition ' + (this.state.logoFrame === frame.value
                                                    ? 'border-sky-400 bg-sky-500 text-slate-950'
                                                    : 'border-white/10 bg-white/5 text-slate-200 hover:bg-white/10')}
                                            >
                                                {frame.icon}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <div className="text-sm font-semibold text-white">Quick tools</div>
                                    <button type="button" disabled={!this.state.source || this.state.processing} className="mt-3 inline-flex w-full items-center justify-center rounded-xl border border-emerald-400/30 bg-emerald-500/15 px-4 py-2.5 text-sm font-semibold text-emerald-200 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50" onClick={() => this._removeBackground()}>
                                        {this.state.processing ? 'Processing...' : 'Remove background'}
                                    </button>
                                    <p className="mt-2 text-xs leading-5 text-slate-400">Best for logos on white or otherwise flat backgrounds. If it struggles, keep the original crop and use a frame shape/background treatment instead.</p>
                                </div>

                                {this.state.error ? <div className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{this.state.error}</div> : null}
                            </div>

                            <div className="border-t border-white/10 px-6 py-4">
                                <div className="flex gap-3">
                                    <button type="button" className="inline-flex flex-1 items-center justify-center rounded-xl border border-white/10 px-4 py-2.5 text-sm font-semibold text-slate-200 transition hover:bg-white/5" onClick={this.props.onClose}>
                                        Cancel
                                    </button>
                                    <button type="button" disabled={!this.state.source || this.state.processing} className="inline-flex flex-1 items-center justify-center rounded-xl bg-[#1cd997] px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50" onClick={() => this._apply()}>
                                        Apply logo
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

export default LogoEditorModal
